import { useState, useEffect, useRef } from 'react';
import { 
  Save, Eye, RefreshCw, Palette, Type, Image, Link, 
  Layout, Settings, ChevronDown, ChevronUp, X, Upload,
  Home, Sparkles, Users, Star, MessageSquare, Phone, Edit3, LogIn
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

// Color presets
const COLOR_PRESETS = [
  { name: 'Kırmızı', value: '#dc2626', gradient: 'from-red-600 to-orange-500' },
  { name: 'Mavi', value: '#2563eb', gradient: 'from-blue-600 to-cyan-500' },
  { name: 'Yeşil', value: '#16a34a', gradient: 'from-green-600 to-emerald-500' },
  { name: 'Mor', value: '#9333ea', gradient: 'from-purple-600 to-pink-500' },
  { name: 'Turuncu', value: '#ea580c', gradient: 'from-orange-600 to-amber-500' },
  { name: 'Teal', value: '#0d9488', gradient: 'from-teal-600 to-cyan-500' },
];

// Font presets  
const FONT_PRESETS = [
  { name: 'Varsayılan', value: 'inherit' },
  { name: 'Inter', value: "'Inter', sans-serif" },
  { name: 'Poppins', value: "'Poppins', sans-serif" },
  { name: 'Roboto', value: "'Roboto', sans-serif" },
];

const DEFAULT_SETTINGS = {
  // Hero Section
  hero: {
    badge_text: "İsveç'in #1 Türkçe Ehliyet Platformu",
    title: 'Ehliyet.nu',
    subtitle: 'Teorifrågor för Körkort på Turkiska i Sverige',
    subtitle_secondary: 'Türkçe Teori Soruları | Teoriprov Körkort Turkiska',
    description: '2,500+ güncel soru • Trafikregler på turkiska • Gratis teorifrågor körkort • Trafikverket uyumlu',
    cta_primary_text: 'Ücretsiz Teste Başla',
    cta_secondary_text: "Premium'a Geç",
    total_questions_display: '2000+', // Premium soru sayısı gösterimi
    stats: [
      { value: '2,500+', label: 'Güncel Soru' },
      { value: '70', label: 'Gerçek Sınav' },
      { value: '%95', label: 'Başarı Oranı' },
      { value: '🇹🇷', label: 'Türkçe Destek' }
    ],
    gradient: 'from-red-950 via-red-900 to-orange-900',
    text_color: '#ffffff'
  },
  
  // Login Page Section
  login_page: {
    title_line1: 'İsveç Ehliyet',
    title_line2: 'Sınavına Hazırlan!',
    subtitle: 'Türkçe açıklamalar, gerçek sınav formatı ve %95 başarı oranı ile ilk seferde geç!',
    stat_questions: 2000,
    stat_success_rate: 95,
    stat_users: 1000
  },
  
  // Features Section
  features: {
    section_title: 'Neden Bizi Tercih Etmelisiniz?',
    section_subtitle: 'İsveç ehliyet sınavına hazırlanmanın en etkili yolu',
    items: [
      { icon: '📝', title: '2000+ Güncel Soru', description: 'Sürekli güncellenen soru bankası' },
      { icon: '🇹🇷', title: 'Türkçe Açıklamalar', description: 'Her sorunun detaylı Türkçe açıklaması' },
      { icon: '📊', title: 'İlerleme Takibi', description: 'Performansınızı anlık takip edin' },
      { icon: '🎯', title: 'Gerçek Sınav Formatı', description: 'Resmi sınav formatına uygun' }
    ],
    background_color: '#f9fafb'
  },
  
  // Testimonials Section
  testimonials: {
    section_title: 'Kullanıcılarımız Ne Diyor?',
    show_section: true
  },
  
  // CTA Section
  cta: {
    title: 'Hemen Başla!',
    subtitle: 'Premium üyelik ile tüm sorulara sınırsız erişim',
    button_text: "Premium'a Geç",
    gradient: 'from-red-600 to-orange-500'
  },
  
  // Footer
  footer: {
    company_name: 'Ehliyet.nu',
    description: 'İsveç ehliyet sınavına hazırlanmanın en kolay yolu',
    contact_email: 'info@ehliyet.nu',
    copyright_text: '© Ehliyet.nu - Tüm hakları saklıdır'
  },
  
  // Global Styles
  global: {
    primary_color: '#dc2626',
    secondary_color: '#ea580c',
    font_family: 'inherit'
  }
};

export default function HomepageAdminTab({ adminPassword }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [showPreview, setShowPreview] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    hero: true,
    features: false,
    testimonials: false,
    cta: false,
    footer: false,
    global: false,
    login_page: false
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API}/admin/homepage/settings`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      if (response.ok) {
        const data = await response.json();
        if (data && Object.keys(data).length > 0) {
          setSettings({ ...DEFAULT_SETTINGS, ...data });
        }
      }
    } catch (error) {
      console.error('Error fetching homepage settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/homepage/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Password': adminPassword
        },
        body: JSON.stringify(settings)
      });
      
      const data = await response.json();
      if (data.success) {
        alert('✅ Ana sayfa ayarları kaydedildi!');
      } else {
        alert('❌ Kayıt başarısız: ' + (data.message || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('❌ Kayıt sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (section, key, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
  };

  const updateNestedSetting = (section, index, key, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        items: prev[section].items.map((item, i) => 
          i === index ? { ...item, [key]: value } : item
        )
      }
    }));
  };

  const updateStatSetting = (index, key, value) => {
    setSettings(prev => ({
      ...prev,
      hero: {
        ...prev.hero,
        stats: prev.hero.stats.map((stat, i) => 
          i === index ? { ...stat, [key]: value } : stat
        )
      }
    }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  const SectionHeader = ({ title, icon: Icon, section }) => (
    <button
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
    >
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5 text-red-600" />
        <span className="font-bold text-gray-900">{title}</span>
      </div>
      {expandedSections[section] ? (
        <ChevronUp className="w-5 h-5 text-gray-500" />
      ) : (
        <ChevronDown className="w-5 h-5 text-gray-500" />
      )}
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">🏠 Ana Sayfa Düzenleyici</h2>
          <p className="text-gray-600">Ana sayfanızı özelleştirin - yazılar, renkler, görseller</p>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={() => window.open('/', '_blank')}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Eye className="w-4 h-4" />
            Önizle
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-4">
        
        {/* Hero Section */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <SectionHeader title="Hero Bölümü (Üst Kısım)" icon={Home} section="hero" />
          
          {expandedSections.hero && (
            <div className="p-6 space-y-6 border-t">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Badge Metni (Üst küçük yazı)
                  </label>
                  <input
                    type="text"
                    value={settings.hero.badge_text || ''}
                    onChange={(e) => updateSetting('hero', 'badge_text', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                    placeholder="İsveç'in #1 Türkçe Ehliyet Platformu"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ana Başlık (Site Adı)
                  </label>
                  <input
                    type="text"
                    value={settings.hero.title || ''}
                    onChange={(e) => updateSetting('hero', 'title', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                    placeholder="Ehliyet.nu"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alt Başlık 1 (İsveççe - SEO için önemli)
                </label>
                <input
                  type="text"
                  value={settings.hero.subtitle || ''}
                  onChange={(e) => updateSetting('hero', 'subtitle', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                  placeholder="Teorifrågor för Körkort på Turkiska i Sverige"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alt Başlık 2 (Türkçe/İsveççe karışık)
                </label>
                <input
                  type="text"
                  value={settings.hero.subtitle_secondary || ''}
                  onChange={(e) => updateSetting('hero', 'subtitle_secondary', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                  placeholder="Türkçe Teori Soruları | Teoriprov Körkort Turkiska"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Açıklama Metni
                </label>
                <textarea
                  value={settings.hero.description || ''}
                  onChange={(e) => updateSetting('hero', 'description', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                  placeholder="2,500+ güncel soru • Trafikregler på turkiska • Gratis teorifrågor körkort"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Birincil Buton Metni
                  </label>
                  <input
                    type="text"
                    value={settings.hero.cta_primary_text || ''}
                    onChange={(e) => updateSetting('hero', 'cta_primary_text', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    İkincil Buton Metni
                  </label>
                  <input
                    type="text"
                    value={settings.hero.cta_secondary_text || ''}
                    onChange={(e) => updateSetting('hero', 'cta_secondary_text', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>
              
              {/* Premium Question Count Display */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                <label className="block text-sm font-medium text-yellow-800 mb-2">
                  💎 Premium Soru Sayısı Gösterimi
                </label>
                <p className="text-xs text-yellow-600 mb-2">
                  Ana sayfada "ÜCRETSİZ SORU + Premium: <strong>XXXX+</strong> Güncel Soru!" olarak görünür
                </p>
                <input
                  type="text"
                  value={settings.hero.total_questions_display || '2000+'}
                  onChange={(e) => updateSetting('hero', 'total_questions_display', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 text-xl font-bold text-center"
                  placeholder="2000+"
                />
              </div>
              
              {/* Stats */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  İstatistik Kutuları (4 adet)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {(settings.hero.stats || []).map((stat, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg">
                      <input
                        type="text"
                        value={stat.value || ''}
                        onChange={(e) => updateStatSetting(index, 'value', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg mb-2 text-xl font-bold text-center"
                        placeholder="2000+"
                      />
                      <input
                        type="text"
                        value={stat.label || ''}
                        onChange={(e) => updateStatSetting(index, 'label', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg text-center text-sm"
                        placeholder="Güncel Soru"
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Live Preview */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Canlı Önizleme
                </label>
                <div className={`bg-gradient-to-r ${settings.hero.gradient} p-8 rounded-xl text-white`}>
                  <span className="inline-block px-4 py-2 bg-white/10 rounded-full text-sm mb-4">
                    {settings.hero.badge_text}
                  </span>
                  <h1 className="text-3xl font-black mb-3">{settings.hero.title}</h1>
                  <p className="text-white/80 mb-6">{settings.hero.subtitle}</p>
                  <div className="flex gap-3">
                    <button className="px-6 py-3 bg-white text-red-600 rounded-xl font-bold">
                      {settings.hero.cta_primary_text}
                    </button>
                    <button className="px-6 py-3 border-2 border-white/30 rounded-xl font-bold">
                      {settings.hero.cta_secondary_text}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Features Section */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <SectionHeader title="Özellikler Bölümü" icon={Sparkles} section="features" />
          
          {expandedSections.features && (
            <div className="p-6 space-y-6 border-t">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bölüm Başlığı
                  </label>
                  <input
                    type="text"
                    value={settings.features.section_title}
                    onChange={(e) => updateSetting('features', 'section_title', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alt Başlık
                  </label>
                  <input
                    type="text"
                    value={settings.features.section_subtitle}
                    onChange={(e) => updateSetting('features', 'section_subtitle', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>
              
              {/* Feature Items */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Özellik Kartları
                </label>
                <div className="space-y-4">
                  {settings.features.items.map((item, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg">
                      <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-2">
                          <label className="block text-xs text-gray-500 mb-1">Emoji</label>
                          <input
                            type="text"
                            value={item.icon}
                            onChange={(e) => updateNestedSetting('features', index, 'icon', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-2xl text-center"
                          />
                        </div>
                        <div className="col-span-4">
                          <label className="block text-xs text-gray-500 mb-1">Başlık</label>
                          <input
                            type="text"
                            value={item.title}
                            onChange={(e) => updateNestedSetting('features', index, 'title', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                        <div className="col-span-6">
                          <label className="block text-xs text-gray-500 mb-1">Açıklama</label>
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => updateNestedSetting('features', index, 'description', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Login Page Section */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <SectionHeader title="Giriş Sayfası Ayarları" icon={LogIn} section="login_page" />
          
          {expandedSections.login_page && (
            <div className="p-6 space-y-6 border-t">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-xl p-4 mb-4">
                <p className="text-red-700 text-sm">
                  🔑 Bu ayarlar giriş/kayıt sayfasındaki "İsveç Ehliyet Sınavına Hazırlan!" bölümünü düzenler.
                </p>
              </div>
              
              {/* Title Lines */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Başlık 1. Satır
                  </label>
                  <input
                    type="text"
                    value={settings.login_page?.title_line1 || 'İsveç Ehliyet'}
                    onChange={(e) => updateSetting('login_page', 'title_line1', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                    placeholder="İsveç Ehliyet"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Başlık 2. Satır
                  </label>
                  <input
                    type="text"
                    value={settings.login_page?.title_line2 || 'Sınavına Hazırlan!'}
                    onChange={(e) => updateSetting('login_page', 'title_line2', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                    placeholder="Sınavına Hazırlan!"
                  />
                </div>
              </div>
              
              {/* Subtitle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alt Yazı
                </label>
                <textarea
                  value={settings.login_page?.subtitle || 'Türkçe açıklamalar, gerçek sınav formatı ve %95 başarı oranı ile ilk seferde geç!'}
                  onChange={(e) => updateSetting('login_page', 'subtitle', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                  rows={2}
                  placeholder="Türkçe açıklamalar..."
                />
              </div>
              
              {/* Stats */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="text-sm font-bold text-gray-700 mb-4">📊 İstatistikler</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Soru Sayısı
                    </label>
                    <input
                      type="number"
                      value={settings.login_page?.stat_questions || 2000}
                      onChange={(e) => updateSetting('login_page', 'stat_questions', parseInt(e.target.value))}
                      className="w-full px-4 py-3 border-2 border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 text-xl font-bold text-center"
                      placeholder="2000"
                    />
                    <p className="text-xs text-gray-500 mt-1 text-center">Güncel Soru</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Başarı Oranı (%)
                    </label>
                    <input
                      type="number"
                      value={settings.login_page?.stat_success_rate || 95}
                      onChange={(e) => updateSetting('login_page', 'stat_success_rate', parseInt(e.target.value))}
                      className="w-full px-4 py-3 border-2 border-emerald-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-xl font-bold text-center"
                      placeholder="95"
                      max={100}
                    />
                    <p className="text-xs text-gray-500 mt-1 text-center">Başarı Oranı</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Kullanıcı Sayısı
                    </label>
                    <input
                      type="number"
                      value={settings.login_page?.stat_users || 1000}
                      onChange={(e) => updateSetting('login_page', 'stat_users', parseInt(e.target.value))}
                      className="w-full px-4 py-3 border-2 border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-xl font-bold text-center"
                      placeholder="1000"
                    />
                    <p className="text-xs text-gray-500 mt-1 text-center">Mutlu Kullanıcı</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Testimonials Section - Quick Edit Link */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <SectionHeader title="Kullanıcı Yorumları (Sınavda Başarılı Oldular)" icon={Users} section="testimonials" />
          
          {expandedSections.testimonials && (
            <div className="p-6 space-y-6 border-t">
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      💬 Kullanıcı Yorumlarını Düzenle
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Ana sayfada görünen "Sınavda Başarılı Oldular" bölümündeki kullanıcı yorumlarını 
                      <strong> Kullanıcı Yorumları</strong> sekmesinden düzenleyebilirsiniz.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={() => {
                          // Find and click the testimonials tab
                          const testimonialsTab = document.querySelector('[data-tab="testimonials"]');
                          if (testimonialsTab) {
                            testimonialsTab.click();
                          } else {
                            // Fallback: scroll to show the user where to click
                            alert('Sol menüden "Kullanıcı Yorumları" sekmesine tıklayın');
                          }
                        }}
                        className="flex items-center gap-2 px-5 py-3 bg-yellow-500 text-white rounded-xl font-bold hover:bg-yellow-600 transition-colors shadow-lg shadow-yellow-500/30"
                      >
                        <Edit3 className="w-5 h-5" />
                        Yorumları Düzenle
                      </button>
                      <button
                        onClick={() => window.open('/#testimonials', '_blank')}
                        className="flex items-center gap-2 px-5 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                      >
                        <Eye className="w-5 h-5" />
                        Önizle
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Section Title Settings */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bölüm Başlığı
                </label>
                <input
                  type="text"
                  value={settings.testimonials?.section_title || 'Kullanıcılarımız Ne Diyor?'}
                  onChange={(e) => updateSetting('testimonials', 'section_title', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                  placeholder="Kullanıcılarımız Ne Diyor?"
                />
              </div>
              
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="show_testimonials"
                  checked={settings.testimonials?.show_section !== false}
                  onChange={(e) => updateSetting('testimonials', 'show_section', e.target.checked)}
                  className="w-5 h-5 text-red-600 rounded focus:ring-red-500"
                />
                <label htmlFor="show_testimonials" className="text-gray-700 font-medium">
                  Bu bölümü ana sayfada göster
                </label>
              </div>
            </div>
          )}
        </div>

        {/* CTA Section */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <SectionHeader title="CTA Bölümü (Çağrı)" icon={Phone} section="cta" />
          
          {expandedSections.cta && (
            <div className="p-6 space-y-6 border-t">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Başlık
                  </label>
                  <input
                    type="text"
                    value={settings.cta.title}
                    onChange={(e) => updateSetting('cta', 'title', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Buton Metni
                  </label>
                  <input
                    type="text"
                    value={settings.cta.button_text}
                    onChange={(e) => updateSetting('cta', 'button_text', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alt Başlık
                </label>
                <input
                  type="text"
                  value={settings.cta.subtitle}
                  onChange={(e) => updateSetting('cta', 'subtitle', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>
              
              {/* Preview */}
              <div className={`bg-gradient-to-r ${settings.cta.gradient} p-8 rounded-xl text-white text-center`}>
                <h2 className="text-2xl font-black mb-2">{settings.cta.title}</h2>
                <p className="text-white/80 mb-4">{settings.cta.subtitle}</p>
                <button className="px-8 py-3 bg-white text-red-600 rounded-xl font-bold">
                  {settings.cta.button_text}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer Section */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <SectionHeader title="Footer (Alt Kısım)" icon={Layout} section="footer" />
          
          {expandedSections.footer && (
            <div className="p-6 space-y-6 border-t">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Şirket Adı
                  </label>
                  <input
                    type="text"
                    value={settings.footer.company_name}
                    onChange={(e) => updateSetting('footer', 'company_name', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    İletişim E-postası
                  </label>
                  <input
                    type="email"
                    value={settings.footer.contact_email}
                    onChange={(e) => updateSetting('footer', 'contact_email', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Açıklama
                </label>
                <textarea
                  value={settings.footer.description}
                  onChange={(e) => updateSetting('footer', 'description', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telif Hakkı Metni
                </label>
                <input
                  type="text"
                  value={settings.footer.copyright_text}
                  onChange={(e) => updateSetting('footer', 'copyright_text', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>
          )}
        </div>

        {/* Global Styles */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <SectionHeader title="Genel Stil Ayarları" icon={Palette} section="global" />
          
          {expandedSections.global && (
            <div className="p-6 space-y-6 border-t">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Ana Renk
                </label>
                <div className="flex flex-wrap gap-3">
                  {COLOR_PRESETS.map(color => (
                    <button
                      key={color.value}
                      onClick={() => updateSetting('global', 'primary_color', color.value)}
                      className={`w-12 h-12 rounded-xl transition-all ${
                        settings.global.primary_color === color.value 
                          ? 'ring-4 ring-offset-2 ring-gray-400 scale-110' 
                          : 'hover:scale-105'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                  <input
                    type="color"
                    value={settings.global.primary_color}
                    onChange={(e) => updateSetting('global', 'primary_color', e.target.value)}
                    className="w-12 h-12 rounded-xl cursor-pointer"
                    title="Özel Renk"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Yazı Tipi
                </label>
                <div className="flex flex-wrap gap-3">
                  {FONT_PRESETS.map(font => (
                    <button
                      key={font.value}
                      onClick={() => updateSetting('global', 'font_family', font.value)}
                      className={`px-4 py-2 rounded-lg transition-all ${
                        settings.global.font_family === font.value 
                          ? 'bg-red-600 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      style={{ fontFamily: font.value }}
                    >
                      {font.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Floating Save Button */}
      <div className="fixed bottom-6 right-6">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-xl shadow-lg hover:bg-green-700 transition-all disabled:opacity-50"
        >
          {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
          {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
        </button>
      </div>
    </div>
  );
}
